/**
 * MediaReadiness — quality control. Upload a master, thumbnail or caption
 * file against an episode and watch the four checks turn green.
 */
import React, { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Upload, Loader2, X } from 'lucide-react';
import api, { getErrorMessage } from '@/lib/api';

const MEDIA = [['master', 'Master video'], ['thumbnail', 'Thumbnail image'], ['captions', 'Caption file']];

export const MediaReadiness = () => {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [episodeId, setEpisodeId] = useState('');
  const [mediaType, setMediaType] = useState('master');
  const [uploading, setUploading] = useState(false);

  const { data } = useQuery({
    queryKey: ['fwtv-media-readiness'],
    queryFn: () => api.get('/fwtv/studio/media/readiness').then((r) => r.data),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['fwtv-media-readiness'] });
    qc.invalidateQueries({ queryKey: ['fwtv-episodes'] });
    qc.invalidateQueries({ queryKey: ['fwtv-studio-overview'] });
  };

  const detach = useMutation({
    mutationFn: ({ id, type }) => api.delete(`/fwtv/studio/episodes/${id}/media/${type}`),
    onSuccess: () => { toast.success('Removed'); refresh(); },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not remove that file')),
  });

  const upload = async (file) => {
    if (!episodeId) { toast.error('Choose an episode first'); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('file_type', mediaType === 'master' ? 'video' : 'event');
      const up = await api.post('/admin/upload', form,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      await api.post(`/fwtv/studio/episodes/${episodeId}/media`, {
        media_type: mediaType, url: up.data.url, filename: up.data.filename,
      });
      toast.success(`${mediaType} attached`);
      refresh();
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) {
      toast.error(getErrorMessage(e, 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const episodes = data?.episodes || [];

  return (
    <div className="space-y-5" data-testid="fwtv-media-readiness">
      <Card>
        <CardContent className="p-5 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <Label>Episode</Label>
            <Select value={episodeId} onValueChange={setEpisodeId}>
              <SelectTrigger data-testid="media-episode"><SelectValue placeholder="Choose an episode" /></SelectTrigger>
              <SelectContent>
                {episodes.map((e) => <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Media type</Label>
            <Select value={mediaType} onValueChange={setMediaType}>
              <SelectTrigger data-testid="media-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MEDIA.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 flex items-end gap-3">
            <input ref={fileRef} type="file" data-testid="media-file"
                   onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
                   className="flex-1 text-sm" />
            <Button disabled={uploading} onClick={() => fileRef.current?.click()}
                    data-testid="media-upload-btn">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {episodes.length === 0 && (
          <p className="text-sm text-slate-500" data-testid="media-empty">
            No episodes yet — add one in the pipeline first.
          </p>
        )}
        {episodes.map((ep) => (
          <Card key={ep.id} data-testid={`media-episode-${ep.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-sm">{ep.title}</p>
                  <p className="text-xs text-slate-500">{ep.programme_title}</p>
                </div>
                <Badge className={ep.readiness.complete
                  ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100'
                  : 'bg-amber-100 text-amber-800 hover:bg-amber-100'}>
                  {ep.readiness.complete ? 'Complete' : 'Incomplete'}
                </Badge>
              </div>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {Object.entries(ep.readiness.checks).map(([key, ok]) => (
                  <span key={key}
                        className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                          ok ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {key}
                  </span>
                ))}
              </div>
              <div className="mt-4 space-y-1.5">
                {MEDIA.filter(([v]) => ep.assets?.[v]).map(([v, l]) => (
                  <div key={v} className="flex items-center justify-between gap-2 text-xs text-slate-600">
                    <span className="truncate">{l}: {ep.assets[v].filename || 'attached'}</span>
                    <button onClick={() => detach.mutate({ id: ep.id, type: v })}
                            data-testid={`media-detach-${ep.id}-${v}`}>
                      <X className="h-3 w-3 text-red-500" />
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default MediaReadiness;
